import { editDocument, getDocument } from '@/services/documents-service/documents-service';
import { UpdateDocumentPayload } from '@/services/documents-service/documents-service.types';
import { openNotification } from '@/utils/notification/notification';
import { ReactQueryKeys } from '@/utils/react-query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useDocument = (id: string) => {
  const queryClient = useQueryClient();

  const { data: documentData, error } = useQuery({
    queryKey: ReactQueryKeys.DOCUMENT(id),
    queryFn: () => getDocument(id),
    enabled: Boolean(id),
  });

  const { mutate: updatedDocument, isPending: isPendingDocumentUpdate } = useMutation({
    mutationFn: (data: UpdateDocumentPayload) => editDocument(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ReactQueryKeys.DOCUMENT(id),
      });

      queryClient.invalidateQueries({
        queryKey: ['document'],
      });

      openNotification({
        message: 'Document has been successfully updated.',
        type: 'success',
      });
    },
    onError: (err) => {
      openNotification({
        message: 'Oops — something went wrong. Please try again.',
        type: 'error',
      });
      return err;
    },
  });

  const updateDocument = (data: UpdateDocumentPayload, callbacks?: { onSuccess?: () => void }) => {
    updatedDocument(data, {
      onSuccess: () => {
        if (callbacks?.onSuccess) {
          callbacks.onSuccess();
        }
      },
    });
  };

  return { documentData, error, updatedDocument: updateDocument, isPendingDocumentUpdate };
};
